import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Space, Typography, List, Modal, Input, Select, Form, Popconfirm, Alert, Tag, Spin, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, DragOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PermissionedButton, PermissionOverlay } from "../../PermissionedComponents";
import { TrainingAPI } from "../../../api/TrainingAPI";
import jsonLogic from 'json-logic-js';
import { PlayersForm, PLAYERS_DEFAULTS } from "@components/DashboardAlt/AddWidgetModal/shared/PlayersForm";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";

jsonLogic.add_operation("contains", function(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') {
    return false;
  }
  return a.includes(b);
});

jsonLogic.add_operation("starts_with", function(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') {
    return false;
  }
  return a.startsWith(b);
});

jsonLogic.add_operation("ends_with", function(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') {
    return false;
  }
  return a.endsWith(b);
});

const { Option } = Select;
const { Text } = Typography;

const SUBJECTS_VAR = { var: "subjects" };

// A rule with a player subset is {"and": [<metric condition>, {"or": [<subject terms>]}]}.
// A guard with a term of another shape makes the whole rule unknown. A partial parse
// would drop that term on the next save without a trace.
const splitRuleCondition = (condition) => {
  const parts = condition?.and;
  if (Array.isArray(parts) && parts.length === 2 && Array.isArray(parts[1]?.or)) {
    const subjects = parts[1].or.map((term) => term?.in?.[0]);
    const known = subjects.every((s) => typeof s === "string");
    return { metricCondition: known ? parts[0] : null, subjects };
  }
  return { metricCondition: condition, subjects: [] };
};

const UNKNOWN_RULES_WARNING = (count) =>
  `${count} saved ${count === 1 ? "rule has" : "rules have"} an unknown shape and ${count === 1 ? "is" : "are"} not shown. The next save removes ${count === 1 ? "it" : "them"}.`;

const wrapWithSubjects = (metricCondition, subjects) => {
  if (!metricCondition || !subjects?.length) return metricCondition;
  return { and: [metricCondition, { or: subjects.map((s) => ({ in: [s, SUBJECTS_VAR] })) }] };
};

// The match endpoint knows metric names only, and it skips an "and" condition.
const metricOnlyLogic = (logicString) => {
  try {
    const parsed = JSON.parse(logicString);
    if (!Array.isArray(parsed?.if)) return logicString;
    const lastIndex = parsed.if.length - 1;
    const body = parsed.if.map((part, index) =>
      index % 2 === 0 && index < lastIndex ? (splitRuleCondition(part).metricCondition ?? part) : part);
    return JSON.stringify({ if: body });
  } catch (e) {
    return logicString;
  }
};

const DELETED_SUBJECT_TAGS = {
  player: { name: "Deleted athlete", color: "red", deleted: true },
  pos: { name: "Deleted position", color: "red", deleted: true },
  group: { name: "Deleted group", color: "red", deleted: true },
};

const DELETED_SUBJECT_WARNING = "This rule names a deleted athlete, position or group. Edit the rule and remove the chip that shows a raw id, such as group-8.";

const SUBJECTS_HELP = "Leave empty to apply the rule to everyone. Select athletes, positions or groups to narrow it.";

// Form.Item gives a string[] value. PlayersForm reads and patches PlayersFormFields.
// "Full Team" means everyone, so a pick of it clears the subset.
const RuleSubjectsField = ({ value, onChange, getSubjectEntities }) => (
  <PlayersForm
    value={{ ...PLAYERS_DEFAULTS, selectedSubjects: value ?? [] }}
    onChange={(patch) => {
      if (patch.selectedSubjects) {
        onChange(patch.selectedSubjects.filter((s) => s !== "full-team"));
      }
    }}
    getSubjectEntities={getSubjectEntities}
    description={SUBJECTS_HELP}
    hideDrills
    size="small"
  />
);

const MetricNamesInfoBubble = ({ matchingNames }) => {
  if (!matchingNames || !Array.isArray(matchingNames) || matchingNames.length === 0) {
    return null;
  }

  const displayNames = matchingNames.slice(0, 5);
  const remainingCount = matchingNames.length - 5;

  const tooltipContent = (
    <div>
      <div style={{ marginBottom: 4, fontWeight: 'bold' }}>Matching Metrics:</div>
      {displayNames.map((name, index) => (
        <div key={index} style={{ marginBottom: 2 }}>
          {name}
        </div>
      ))}
      {remainingCount > 0 && (
        <div style={{ marginTop: 4, fontStyle: 'italic' }}>
          and {remainingCount} more...
        </div>
      )}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} overlayStyle={{ maxWidth: 400 }}>
      <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', marginLeft: 4 }} />
    </Tooltip>
  );
};

const SortableRuleItem = ({ rule, strategies, getUserRuleMatchCount, getUserRuleMatchingNames, loadingCounts, onEdit, onDelete, getSubjectTag, orderWarning }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
  });

  const matchCount = getUserRuleMatchCount(rule);
  const matchingNames = getUserRuleMatchingNames(rule);
  const tags = (rule.subjects ?? []).map((s) => ({ id: s, ...getSubjectTag(s) }));

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: 8,
        backgroundColor: isDragging ? '#f0f0f0' : 'white',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        padding: '12px 16px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <div
            {...listeners}
            style={{ marginRight: 12, cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <DragOutlined />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4 }}>
              <Space>
                <Text>
                  {rule.matchType === 'exact' ? 'Is equal to' :
                   rule.matchType === 'not_equal' ? 'Is not equal to' :
                   rule.matchType === 'contains' ? 'Contains' :
                   rule.matchType === 'starts_with' ? 'Starts with' :
                   rule.matchType === 'ends_with' ? 'Ends with' :
                   rule.matchType} "{rule.metricName}" → {strategies.find(s => s.id === rule.colorStrategyId)?.name || 'Unknown Strategy'}
                </Text>
                {matchCount !== undefined ? (
                  <Space size={4}>
                    <Tag color={matchCount > 0 ? "green" : "orange"}>
                      {matchCount} matches
                    </Tag>
                    <MetricNamesInfoBubble matchingNames={matchingNames} />
                  </Space>
                ) : loadingCounts ? (
                  <Tag color="blue">Loading...</Tag>
                ) : null}
              </Space>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>Players:</Text>
              {tags.length
                ? tags.map((tag) => <Tag key={tag.id} color={tag.color}>{tag.name}</Tag>)
                : <Tag>Everyone</Tag>}
            </div>
            {tags.some((tag) => tag.deleted) && (
              <Alert type="warning" showIcon message={DELETED_SUBJECT_WARNING} style={{ marginTop: 6, padding: "2px 8px" }} />
            )}
            {orderWarning && (
              <Alert type="warning" showIcon message={orderWarning} style={{ marginTop: 6, padding: "2px 8px" }} />
            )}
          </div>
        </div>
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => onEdit(rule)}
          />
          <Popconfirm
            title="Are you sure you want to delete this rule?"
            onConfirm={() => onDelete(rule.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            />
          </Popconfirm>
        </Space>
      </div>
    </div>
  );
};

export const GymMetricColorGroupings = ({ onSave }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form] = Form.useForm();
  const watchedSubjects = Form.useWatch("subjects", form) ?? [];
  const [userRules, setUserRules] = useState([]);
  const [liveMatchCount, setLiveMatchCount] = useState(null);
  const [liveMatchingNames, setLiveMatchingNames] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [loadingLiveCount, setLoadingLiveCount] = useState(false);
  const [systemLogicParts, setSystemLogicParts] = useState([]);
  const [logicMatchesDict, setLogicMatchesDict] = useState({});

  const trainingAPI = new TrainingAPI();

  const {
    color_strategies: strategies,
    info: { gym_metric_color_logic: gymMetricColorLogic },
    allPlayers = [],
    positionEntities = [],
    playerGroups = [],
  } = useSelector(({ team }) => team);

  // Retired athletes stay selectable: their historical rows still match a rule.
  const getSubjectEntities = useCallback((subjectType) => {
    if (subjectType === "player") {
      return allPlayers.map((p) => ({
        id: String(p.player_id),
        name: `${p.first_name} ${p.last_name}${p.retired ? " (retired)" : ""}`,
        detail: p.position ?? "",
      }));
    }
    if (subjectType === "position") {
      return positionEntities.map((pos) => ({ id: pos.id, name: pos.name, detail: "" }));
    }
    if (subjectType === "group") {
      return (playerGroups ?? []).map((g) => ({ id: String(g.player_group_id), name: g.name, detail: "" }));
    }
    return [];
  }, [allPlayers, positionEntities, playerGroups]);

  // Retired athletes count too: the picker offers them and their rows still match.
  const countAthletesInSubset = useCallback((subjects) => {
    const wanted = new Set(subjects);
    return allPlayers.filter((p) =>
      wanted.has(`player-${p.player_id}`)
      || wanted.has(`pos-${p.position_id}`)
      || (p.player_groups ?? []).some((g) => wanted.has(`group-${g}`))).length;
  }, [allPlayers]);

  // Function to create JSONLogic for a single rule
  const createRuleLogic = (rule) => {
    if (rule.matchType === 'exact') {
      return {"==": [{"var": "metric_name"}, rule.metricName]};
    } else if (rule.matchType === 'not_equal') {
      return {"!=": [{"var": "metric_name"}, rule.metricName]};
    } else if (rule.matchType === 'contains') {
      return {"contains": [{"var": "metric_name"}, rule.metricName]};
    } else if (rule.matchType === 'starts_with') {
      return {"starts_with": [{"var": "metric_name"}, rule.metricName]};
    } else if (rule.matchType === 'ends_with') {
      return {"ends_with": [{"var": "metric_name"}, rule.metricName]};
    }
    return null;
  };

  const getSystemRulesForDisplay = () => {
    const displayRules = [];

    displayRules.push({
      metricName: "All Others (Default)",
      strategyId: systemLogicParts[0], // strategy id 28
      type: "fallback"
    });

    return displayRules;
  };

  // get matching metric names for a user rule
  const getUserRuleMatchingNames = useCallback((rule) => {
    const logic = createRuleLogic(rule);
    if (logic) {
      const logicString = JSON.stringify(logic);
      return logicMatchesDict[logicString] || [];
    }
    return [];
  }, [logicMatchesDict]);

  // get match count for a user rule (derived from names array length)
  const getUserRuleMatchCount = useCallback((rule) => {
    const matchingNames = getUserRuleMatchingNames(rule);
    return matchingNames.length > 0 ? matchingNames.length : undefined;
  }, [getUserRuleMatchingNames]);

  const subjectTags = useMemo(() => {
    const tags = new Map();
    allPlayers.forEach((p) => tags.set(`player-${p.player_id}`, { name: `${p.first_name} ${p.last_name}`, color: "blue" }));
    positionEntities.forEach((pos) => tags.set(`pos-${pos.id}`, { name: pos.name, color: "green" }));
    (playerGroups ?? []).forEach((g) => tags.set(`group-${g.player_group_id}`, { name: g.name, color: "orange" }));
    return tags;
  }, [allPlayers, positionEntities, playerGroups]);

  // An empty list can still be in flight (player groups load in the background), so a
  // subject is marked deleted only when its list has loaded.
  const getSubjectTag = useCallback((subject) => {
    const known = subjectTags.get(subject);
    if (known) return known;
    const kind = subject.split("-")[0];
    const loadedCount = { player: allPlayers.length, pos: positionEntities.length, group: playerGroups?.length ?? 0 }[kind];
    return loadedCount ? DELETED_SUBJECT_TAGS[kind] : { name: subject, color: "default" };
  }, [subjectTags, allPlayers, positionEntities, playerGroups]);

  // A subset rule never applies when a rule for everyone above it matches every metric it matches.
  const getOrderWarning = useCallback((rule) => {
    if (!rule.subjects?.length) return null;
    const names = getUserRuleMatchingNames(rule);
    if (names.length === 0) return null;
    const index = userRules.findIndex((r) => r.id === rule.id);
    const blocker = userRules.slice(0, index).find((above) => {
      if (above.subjects?.length) return false;
      const aboveNames = new Set(getUserRuleMatchingNames(above));
      return names.every((n) => aboveNames.has(n));
    });
    return blocker
      ? `This rule never applies. The "${blocker.metricName}" rule for everyone above it matches first. Drag this rule up.`
      : null;
  }, [userRules, getUserRuleMatchingNames]);

  // fetch all matching metrics by sending the entire gym_metric_color_logic string
  const fetchAllExistingCounts = useCallback(async () => {
    if (!gymMetricColorLogic) {
      setLogicMatchesDict({});
      return;
    }

    setLoadingCounts(true);

    try {
      const result = await trainingAPI.getGymMetricColorLogicMatches(metricOnlyLogic(gymMetricColorLogic));
      const rulesWithNames = result?.rules || {};
      const newMatchesDict = {};

      Object.keys(rulesWithNames).forEach(logicString => {
        const namesList = rulesWithNames[logicString];
        if (Array.isArray(namesList)) {
          newMatchesDict[logicString] = namesList;
        } else {
          // Fallback for unexpected format
          newMatchesDict[logicString] = [];
        }
      });

      setLogicMatchesDict(newMatchesDict);

    } catch (error) {
      console.error("Error fetching matching metrics:", error);
      setLogicMatchesDict({});
    } finally {
      setLoadingCounts(false);
    }
  }, [gymMetricColorLogic]);

  // parse a single condition and extract match type and metric name
  const parseCondition = (condition) => {
    if (!condition || typeof condition !== 'object') {
      return null;
    }

    let matchType = null;
    let metricName = '';

    if (condition?.["=="]?.[0]?.["var"] === "metric_name") {
      matchType = 'exact';
      metricName = condition["=="][1];
    }
    else if (condition?.["!="]?.[0]?.["var"] === "metric_name") {
      matchType = 'not_equal';
      metricName = condition["!="][1];
    }
    else if (condition?.["contains"]?.[0]?.["var"] === "metric_name") {
      matchType = 'contains';
      metricName = condition["contains"][1];
    }
    else if (condition?.["starts_with"]?.[0]?.["var"] === "metric_name") {
      matchType = 'starts_with';
      metricName = condition["starts_with"][1];
    }
    else if (condition?.["ends_with"]?.[0]?.["var"] === "metric_name") {
      matchType = 'ends_with';
      metricName = condition["ends_with"][1];
    }

    return metricName ? { matchType, metricName } : null;
  };

  useEffect(() => {
    // parse the existing gym_metric_color_logic to extract user-created rules
    if (gymMetricColorLogic) {
      try {
        const parsedLogic = JSON.parse(gymMetricColorLogic);
        if (parsedLogic.if && Array.isArray(parsedLogic.if)) {
          const allRules = parsedLogic.if;

          // The last index is always the system rule (default color strategy 28)
          // All other elements are user-defined rules in condition-strategyId pairs
          const systemRule = allRules[allRules.length - 1];
          const userRuleElements = allRules.slice(0, -1); // all elements except the last one
          const extractedUserRules = [];
          let unknownRuleCount = 0;

          // extract user rules (pairs of condition and strategyId)
          for (let i = 0; i < userRuleElements.length; i += 2) {
            if (i + 1 < userRuleElements.length) {
              const condition = userRuleElements[i];
              const strategyId = userRuleElements[i + 1];

              const { metricCondition, subjects } = splitRuleCondition(condition);
              const parsedCondition = parseCondition(metricCondition);
              if (parsedCondition) {
                extractedUserRules.push({
                  id: `rule_${i}`,
                  metricName: parsedCondition.metricName,
                  colorStrategyId: strategyId,
                  matchType: parsedCondition.matchType,
                  subjects
                });
              } else {
                unknownRuleCount += 1;
                console.warn("Unknown rule shape:", condition);
              }
            }
          }

          if (unknownRuleCount > 0) {
            message.warning(UNKNOWN_RULES_WARNING(unknownRuleCount));
          }
          setUserRules(extractedUserRules);
          setSystemLogicParts([systemRule]);
        }
      } catch (error) {
        console.error("Error parsing gym_metric_color_logic:", error);
      }
    } else {
      setUserRules([]);
      setSystemLogicParts([]);
    }
  }, [gymMetricColorLogic]);

  useEffect(() => {
    if (gymMetricColorLogic) {
      fetchAllExistingCounts();
    }
  }, [gymMetricColorLogic, fetchAllExistingCounts]);

  const handleAdd = () => {
    setEditingRule(null);
    setLiveMatchCount(null);
    setLiveMatchingNames(null);
    setLoadingLiveCount(false);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setLiveMatchCount(null);
    setLiveMatchingNames(null);
    setLoadingLiveCount(false);
    form.setFieldsValue({
      metricName: rule.metricName,
      colorStrategyId: rule.colorStrategyId,
      matchType: rule.matchType,
      subjects: rule.subjects ?? []
    });
    setIsModalVisible(true);
    debouncedFetchLiveCount(rule.metricName, rule.matchType);
  };

  const handleDelete = async (ruleId) => {
    const updatedRules = userRules.filter(rule => rule.id !== ruleId);
    setUserRules(updatedRules);
    await saveToBackend(updatedRules);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      let updatedRules;
      if (editingRule) {
        updatedRules = userRules.map(rule =>
          rule.id === editingRule.id
            ? { ...rule, ...values }
            : rule
        );
      } else {
        const newRule = {
          id: `rule_${Date.now()}`,
          ...values
        };
        updatedRules = [newRule, ...userRules];
      }

      setUserRules(updatedRules);
      await saveToBackend(updatedRules);
      setIsModalVisible(false);
      setEditingRule(null);
      setLiveMatchCount(null);
      setLiveMatchingNames(null);
      setLoadingLiveCount(false);
      form.resetFields();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingRule(null);
    setLiveMatchCount(null);
    setLiveMatchingNames(null);
    setLoadingLiveCount(false);
    form.resetFields();
  };

  const saveToBackend = async (rules) => {
    const userLogicParts = [];
    rules.forEach(rule => {
      const metricCondition = createRuleLogic(rule);
      if (metricCondition) {
        userLogicParts.push(wrapWithSubjects(metricCondition, rule.subjects));
        userLogicParts.push(rule.colorStrategyId);
      }
    });

    const systemRule = systemLogicParts[0]; // default strategy id 28
    const newLogic = {
      "if": [
        ...userLogicParts,
        systemRule
      ]
    };

    if (onSave) {
      await onSave({ gym_metric_color_logic: JSON.stringify(newLogic) });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDrop = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = userRules.findIndex((rule) => rule.id === active.id);
    const newIndex = userRules.findIndex((rule) => rule.id === over.id);
    const updatedRules = arrayMove(userRules, oldIndex, newIndex);
    setUserRules(updatedRules);
    saveToBackend(updatedRules);
  };

  const fetchMatchCount = async (logic) => {
    try {
      let fullLogicString;

      const existingLogic = JSON.parse(metricOnlyLogic(gymMetricColorLogic));
      const modifiedLogic = {
        "if": [
          logic, // New rule condition
          1,     // Dummy strategy ID for the new rule
          ...existingLogic.if // Existing rules
        ]
      };
      fullLogicString = JSON.stringify(modifiedLogic);

      const result = await trainingAPI.getGymMetricColorLogicMatches(fullLogicString);
      const logicString = JSON.stringify(logic);
      const matchingNames = result?.rules?.[logicString] || [];

      return {
        count: Array.isArray(matchingNames) ? matchingNames.length : 0,
        names: Array.isArray(matchingNames) ? matchingNames : []
      };
    } catch (error) {
      console.error("Error fetching match count:", error);
      return { count: 0, names: [] };
    }
  };

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const debouncedFetchLiveCount = useCallback(
    debounce(async (metricName, matchType) => {
      if (!metricName || !matchType) {
        setLiveMatchCount(null);
        setLiveMatchingNames(null);
        setLoadingLiveCount(false);
        return;
      }

      setLoadingLiveCount(true);
      try {
        const logic = createRuleLogic({ metricName, matchType });
        if (logic) {
          const logicString = JSON.stringify(logic);

          const matchingNames = logicMatchesDict[logicString];
          if (matchingNames !== undefined) {
            setLiveMatchCount(matchingNames.length);
            setLiveMatchingNames(matchingNames);
          } else {
            // make API call if not in dictionary
            const result = await fetchMatchCount(logic);
            setLiveMatchCount(result.count);
            setLiveMatchingNames(result.names);
            setLogicMatchesDict(prev => ({ ...prev, [logicString]: result.names }));
          }
        }
      } catch (error) {
        console.error("Error fetching live match count:", error);
        setLiveMatchCount(null);
        setLiveMatchingNames(null);
      } finally {
        setLoadingLiveCount(false);
      }
    }, 300),
    [logicMatchesDict]
  );

  return (
    <Card title="Forceplate/Testing Metric Color Groupings" style={{ marginTop: 24 }}>
      <PermissionOverlay
        permissionCheck="EditColorStrategy"
        tooltipMessage="You don't have permission to edit forceplate/testing metric color groupings"
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>

          <Alert
            message="Rule Priority"
            description="Rules are evaluated in order from top to bottom. The first rule that matches the metric and the athlete is applied. Drag rules to reorder them. Put a rule for a subset of players above a rule for everyone. Default system rules are always evaluated last."
            type="info"
            showIcon
          />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <PermissionedButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              permissionCheck="EditColorStrategy"
            >
              Add Rule
            </PermissionedButton>
          </div>

          {/* User-created rules */}
          {userRules.length > 0 && (
            <div>
              <Text strong>Custom Rules (Evaluated First):</Text>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDrop}
              >
                <SortableContext
                  items={userRules.map((rule) => rule.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div style={{ marginTop: 8 }}>
                    {userRules.map((rule) => (
                      <SortableRuleItem
                        key={rule.id}
                        rule={rule}
                        strategies={strategies}
                        getUserRuleMatchCount={getUserRuleMatchCount}
                        getUserRuleMatchingNames={getUserRuleMatchingNames}
                        loadingCounts={loadingCounts}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        getSubjectTag={getSubjectTag}
                        orderWarning={getOrderWarning(rule)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}

          {/* Default system rules (read-only) */}
          {systemLogicParts.length > 0 && (
            <div>
              <Text strong>System Rules (Default):</Text>
              <List
                style={{ marginTop: 8 }}
                itemLayout="horizontal"
                dataSource={getSystemRulesForDisplay()}
                renderItem={(rule, index) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text type="secondary">
                            {rule.type === 'fallback'
                              ? `All Others (Fallback) → ${strategies.find(s => s.id === rule.strategyId)?.name} (Default)`
                              : `${rule.matchType === 'exact' ? 'Is equal to' :
                                   rule.matchType === 'not_equal' ? 'Is not equal to' :
                                   rule.matchType === 'contains' ? 'Contains' :
                                   rule.matchType === 'starts_with' ? 'Starts with' :
                                   rule.matchType === 'ends_with' ? 'Ends with' :
                                   rule.matchType} "${rule.metricName}" → ${strategies.find(s => s.id === rule.strategyId)?.name || `Strategy ID: ${rule.strategyId}`}`
                            }
                          </Text>
                        </Space>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {rule.type === 'fallback'
                            ? 'Applied when no other rules match'
                            : 'System default rule (cannot be modified)'
                          }
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          )}

          {/* Modal for adding/editing rules */}
          <Modal
            title={editingRule ? "Edit Rule" : "Add New Rule"}
            open={isModalVisible}
            onOk={handleModalOk}
            onCancel={handleModalCancel}
            destroyOnHidden
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                matchType: 'exact',
                subjects: []
              }}
              onValuesChange={(changedValues, allValues) => {
                const relevantFieldChanged = changedValues.metricName !== undefined || changedValues.matchType !== undefined;

                if (relevantFieldChanged) {
                  if (allValues.metricName && allValues.matchType) {
                    debouncedFetchLiveCount(allValues.metricName, allValues.matchType);
                  } else {
                    setLiveMatchCount(null);
                    setLiveMatchingNames(null);
                    setLoadingLiveCount(false);
                  }
                }
              }}
            >
              <Form.Item
                name="metricName"
                label="Metric Name"
                rules={[{ required: true, message: 'Please enter a metric name' }]}
              >
                <Input placeholder="Enter metric name to match" />
              </Form.Item>

              <Form.Item
                name="matchType"
                label="Match Type"
                rules={[{ required: true, message: 'Please select a match type' }]}
              >
                <Select placeholder="Select how to match the metric name">
                  <Option value="exact">Exact Match</Option>
                  <Option value="not_equal">Not Equal</Option>
                  <Option value="contains">Contains</Option>
                  <Option value="starts_with">Starts With</Option>
                  <Option value="ends_with">Ends With</Option>
                </Select>
              </Form.Item>

              {/* Live match count display */}
              {(loadingLiveCount || liveMatchCount !== null) && (
                <Alert
                  message={
                    <Space>
                      <Text>Preview:</Text>
                      {loadingLiveCount ? (
                        <Space>
                          <Spin size="small" />
                          <Text type="secondary">Checking matches...</Text>
                        </Space>
                      ) : (
                        <Space size={4}>
                          <Tag color={liveMatchCount > 0 ? "green" : "orange"}>
                            {liveMatchCount} matching metrics found
                          </Tag>
                          <MetricNamesInfoBubble matchingNames={liveMatchingNames} />
                        </Space>
                      )}
                    </Space>
                  }
                  type={loadingLiveCount ? "info" : (liveMatchCount > 0 ? "success" : "warning")}
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Form.Item name="subjects" label="Players">
                <RuleSubjectsField getSubjectEntities={getSubjectEntities} />
              </Form.Item>

              {watchedSubjects.length > 0 && (
                <Text type="secondary" style={{ display: "block", marginTop: -16, marginBottom: 16, fontSize: 12 }}>
                  Applies to {countAthletesInSubset(watchedSubjects)} of {allPlayers.length} athletes.
                </Text>
              )}

              <Form.Item
                name="colorStrategyId"
                label="Color Strategy"
                rules={[{ required: true, message: 'Please select a color strategy' }]}
              >
                <Select placeholder="Select color strategy to apply">
                  {strategies
                    .filter(strategy => {
                      // Filter out strategies with logic containing GPS-specific variables
                      // as these variables are not supported for TestingMetric datapoints
                      if (!strategy.logic) return true;
                      const gpsOnlyVariables = ['metric_baseline'];
                      return !gpsOnlyVariables.some(variable => strategy.logic.includes(variable));
                    })
                    .map(strategy => (
                      <Option key={strategy.id} value={strategy.id}>
                        {strategy.name}
                      </Option>
                    ))
                  }
                </Select>
              </Form.Item>
            </Form>
          </Modal>

        </Space>
      </PermissionOverlay>
    </Card>
  );
};
